const tryParseJson = (value) => {
  if (typeof value !== 'string') return null

  const trimmedValue = value.trim()
  if (!trimmedValue) return null
  if (!['{', '[', '"'].includes(trimmedValue[0])) return null

  try {
    return JSON.parse(trimmedValue)
  } catch (error) {
    return null
  }
}

const isNotificationRecord = (value) => {
  if (!value || Array.isArray(value) || typeof value !== 'object') return false

  return Boolean(
    value.notificationId ||
      value.notificationType ||
      value.createdTimeStamp ||
      value.updatedTimeStamp ||
      value.expiryDate ||
      value.subject,
  )
}

const parseMessageValue = (value) => {
  if (value == null) return null

  if (Array.isArray(value)) return value
  if (typeof value === 'object') return value

  const message = String(value).trim()
  if (!message) return null

  const parsed = tryParseJson(message)
  if (Array.isArray(parsed)) return parsed
  if (parsed && typeof parsed === 'object') return parsed
  if (parsed != null) return { description: String(parsed) }

  return { description: message }
}

export const parseNotificationMessage = (notification) => {
  if (typeof notification === 'string') return parseMessageValue(notification)
  if (!notification || typeof notification !== 'object') return null

  const rawMessage = notification.messages ?? notification.message ?? notification.body

  if (rawMessage == null) return notification

  return parseMessageValue(rawMessage)
}

export const normalizeNotificationText = (value) => {
  if (value == null) return ''
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

export const isNotificationUnread = (notification) => {
  const status = normalizeNotificationText(notification?.status).toLowerCase()
  return !['read', 'seen', 'viewed'].includes(status)
}

export const getNotificationList = (notifications) => {
  if (notifications == null) return []

  if (Array.isArray(notifications)) {
    return notifications.flatMap((notification) => getNotificationList(notification))
  }

  if (typeof notifications === 'string') {
    const parsedNotifications = tryParseJson(notifications)

    if (parsedNotifications != null) return getNotificationList(parsedNotifications)

    return [{ message: notifications }]
  }

  if (typeof notifications !== 'object') return [{ message: notifications }]

  if (isNotificationRecord(notifications)) return [notifications]

  if (notifications.response != null) return getNotificationList(notifications.response)
  if (notifications.data != null) return getNotificationList(notifications.data)
  if (notifications.notifications != null) return getNotificationList(notifications.notifications)

  const wrappedMessage = notifications.messages ?? notifications.message

  if (wrappedMessage != null) return getNotificationList(wrappedMessage)

  if (
    notifications.title ||
    notifications.description ||
    notifications.body ||
    notifications.route ||
    notifications.action
  ) {
    return [notifications]
  }

  return []
}

export const getParsedNotificationItems = (notifications, { onlyUnread = false } = {}) =>
  getNotificationList(notifications)
    .filter((notification) => !onlyUnread || isNotificationUnread(notification))
    .map((notification) => {
      const parsedMessage = parseNotificationMessage(notification)
      if (!parsedMessage) return null

      const title = normalizeNotificationText(
        parsedMessage.title || parsedMessage.subject || notification?.subject,
      )
      const description = normalizeNotificationText(
        parsedMessage.description || parsedMessage.message || parsedMessage.body,
      )
      const fallbackText = normalizeNotificationText(parsedMessage)
      const text = [title, description || fallbackText].filter(Boolean).join(': ')

      if (!text) return null

      return {
        id: notification?.notificationId || `${title}-${description || fallbackText}`,
        notificationId: notification?.notificationId,
        title,
        description: description || fallbackText,
        text,
        action: normalizeNotificationText(parsedMessage.action),
        isUnread: isNotificationUnread(notification),
        route: normalizeNotificationText(parsedMessage.route),
      }
    })
    .filter(Boolean)
